import {
    ChangeDetectorRef, Directive,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild
} from "@angular/core";
import {FUIAbstractInput} from "../../framework/abstract-input";
import {Subscription} from "rxjs";
import {TranslateService} from "@ngx-translate/core";
import {ColorPickerDirective} from "ngx-color-picker";
import {SafeChangeDetectorRef} from "../../color/safe-change-detector-ref";
import {FUIEventsService} from "../../../events/events.service";
import {FUIColorPair} from "../color-pair.model";

@Directive()
export abstract class FUIAbstractColorPairInput extends FUIAbstractInput implements OnInit, OnDestroy {
    @Input() okLabel: string;
    @Input() resetLabel: string;
    @Input() backgroundEditable: boolean = true;
    @Input() foregroundEditable: boolean = true;
    @Input() allowBackgroundAlpha: boolean = false;
    @Input() allowForegroundAlpha: boolean = false;
    @Input() showClearIcon: boolean = true;
    @Output() onClear: EventEmitter<void> = new EventEmitter<void>();
    @Input() backgroundSelected: boolean;
    @Output() backgroundSelectedChange: EventEmitter<boolean> = new EventEmitter<boolean>();

    pickerColor: FUIColorPair = {backgroundColor: "#ffffff", foregroundColor: "#000000"};

    // color picker not always picking up result when binding to the result of the translate pipe
    translatedOkLabel: string;
    translatedResetLabel: string;

    private _$click: Subscription;
    private _popped: boolean;
    private clicking = false;

    constructor(public elementRef: ElementRef,
                protected eventsService: FUIEventsService,
                protected translateService: TranslateService,
                changeDetectorRef: ChangeDetectorRef) {
        super(changeDetectorRef);
    }

    ngOnInit(): void {
        this.translateService.get(this.okLabel).subscribe(translation => {
            this.translatedOkLabel = translation;
            this.translateService.get(this.resetLabel).subscribe(translation => this.translatedResetLabel = translation);
        });
        this._$click = this.eventsService.click$.subscribe(
            (click) => {
                if (!this.clicking && this.popped && !this.elementRef.nativeElement.contains(click.target)) {
                    this.popped = false;
                }
            }
        );
        if (!this.backgroundEditable) {
            this.backgroundSelected = false;
        } else if (this.backgroundSelected == undefined) {
            this.backgroundSelected = true;
        }
    }

    @ViewChild("colorPicker", {read: ColorPickerDirective})
    set colorPickerDirective(colorPickerDirective: ColorPickerDirective) {
        if (colorPickerDirective) {
            const component: any = (<any> colorPickerDirective).cmpRef._component;
            if (component && component.cdr) {
                component.cdr = new SafeChangeDetectorRef(component.cdr);
            }
        }
    }

    override ngOnDestroy(): void {
        super.ngOnDestroy();
        this._$click.unsubscribe();
    }

    /**
     *
     * @returns {boolean}
     */
    get popped(): boolean {
        return this._popped;
    }

    /**
     *
     * @param {boolean} popped
     */
    set popped(popped: boolean) {
        this._popped = popped;
        this.focussed = popped;
        this.focussed$.emit(this.focussed);
    }

    /**
     *
     * @param {boolean} popped
     */
    public pop(popped: boolean): void {
        this.popped = popped;
    }

    /**
     * Click action is different to other controls, it only wants to pop up it's dropdown, not focus on the underlying input
     */
    public override click(): void {
        this.clicking = true;
        setTimeout(() => this.clicking = false);
        this.popped = !this.popped;
    }

    /**
     * If a user presses enter or space while focussed on the element,
     * use the click method to apply the relevant interaction
     * @param {KeyboardEvent} keyboardEvent
     */
    @HostListener("keypress", ["$event"])
    public keypress(keyboardEvent: KeyboardEvent) {
        if (!this.disabled) {
            if (keyboardEvent.code === "Enter" || keyboardEvent.code === "Space" || keyboardEvent.key === "Enter" || keyboardEvent.key === " ") {
                this.click();
                keyboardEvent.preventDefault();
                keyboardEvent.stopPropagation();
            }
        }
    }

    colorSelected(color: string) {
        super.value = {backgroundColor: this.pickerColor.backgroundColor, foregroundColor: this.pickerColor.foregroundColor};
        this.popped = false;
    }

    override valueEqual(value1: FUIColorPair, value2: FUIColorPair): boolean {
        if (value1 == undefined && value2 == undefined) {
            return true;
        }
        if (value1 == undefined || value2 == undefined) {
            return false;
        }
        return value1.backgroundColor === value2.backgroundColor && value1.foregroundColor === value2.foregroundColor;
    }

    override get value(): FUIColorPair {
        return super.value;
    }

    override set value(value: FUIColorPair) {
        this.setPickerColor(value);
        super.value = value;
    }

    override writeValue(value: FUIColorPair): void {
        this.setPickerColor(value);
        super.writeValue(value);
    }

    setPickerColor(value: FUIColorPair) {
        if (!value) {
            this.pickerColor = {backgroundColor: "#ffffff", foregroundColor: "#000000"};
            return;
        }
        if (value.backgroundColor === "#00000000") {
            this.pickerColor.backgroundColor = "#ffffffff";
        } else if (value.backgroundColor) {
            this.pickerColor.backgroundColor = value.backgroundColor;
        }
        if (value.foregroundColor === "#00000000") {
            this.pickerColor.foregroundColor = "#000000ff";
        } else if (value.foregroundColor) {
            this.pickerColor.foregroundColor = value.foregroundColor;
        }
    }

    get textValue(): string {
        return !this.value ? ""
            : !this.backgroundEditable ? this.value.foregroundColor
                : !this.foregroundEditable ? this.value.backgroundColor
                    : this.value.backgroundColor + " " + this.value.foregroundColor;
    }

    set textValue(textValue: string) {
    }

    clear(event: Event): void {
        event.stopPropagation();
        this.onClear.next();
    }

    get pickerColorValue(): string {
        return this.backgroundSelected ? this.pickerColor.backgroundColor : this.pickerColor.foregroundColor;
    }

    set pickerColorValue(value: string) {
        if (this.backgroundSelected) {
            this.pickerColor.backgroundColor = value;
        } else {
            this.pickerColor.foregroundColor = value;
        }
    }

    get toggleBackgroundSelected(): boolean {
        return this.backgroundSelected;
    }

    set toggleBackgroundSelected(value: boolean) {
        this.backgroundSelected = value;
        this.backgroundSelectedChange.next(value);
        this.changeDetectorRef.detectChanges();
    }

    backgroundClicked() {
        if (this.backgroundEditable) {
            this.backgroundSelected = true;
        }
        this.popped = true;
    }

    foregroundClicked() {
        if (this.foregroundEditable) {
            this.backgroundSelected = false;
        }
        this.popped = true;
    }

    get allowAlpha(): "always" | "disabled" {
        if (this.backgroundSelected) {
            return this.allowBackgroundAlpha ? "always" : "disabled";
        } else {
            return this.allowForegroundAlpha ? "always" : "disabled";
        }
    }
}
